/**
 * SEO Manager
 *
 * Handles robots.txt generation, sitemap.xml generation, and SEO utilities.
 */

import { loadScriptsSettings } from './siteSettings';

export interface SitemapNode {
  path: string;
  title: string;
  changefreq?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  priority?: number;
  children?: SitemapNode[];
}

const MARKETING_ROUTE_TREE: SitemapNode[] = [
  { path: '/', title: 'Home', changefreq: 'daily', priority: 1.0 },
  {
    path: '/features',
    title: 'Features',
    changefreq: 'weekly',
    priority: 0.95,
    children: [
      { path: '/features/lead-tracking', title: 'Lead Tracking & CRM' },
      { path: '/features/canvassing-widget', title: 'Canvassing & Territory' },
      { path: '/features/crm', title: 'CRM & Contacts' },
      { path: '/features/estimating', title: 'Estimating' },
      { path: '/features/marketing-automation', title: 'Marketing Automation' },
      { path: '/features/marketing-dashboard', title: 'Marketing Dashboard' },
      { path: '/features/ai-roofing-crm', title: 'AI-Ready CRM' },
      { path: '/features/invoicing', title: 'Invoicing & Payments' },
      { path: '/features/proposals', title: 'Proposal Builder' },
      { path: '/features/project-management', title: 'Project Management' },
      { path: '/features/storm-canvassing', title: 'Storm & Canvassing' },
      { path: '/features/scheduling', title: 'Job Scheduling' },
      { path: '/features/seed-catalogs-templates', title: 'Seed Catalogs & Templates' },
    ],
  },
  { path: '/for-roofers', title: 'For Roofers', changefreq: 'weekly', priority: 0.9 },
  { path: '/contractors', title: 'For Contractors', changefreq: 'weekly', priority: 0.9 },
  { path: '/agencies', title: 'For Agencies', changefreq: 'weekly', priority: 0.9 },
  { path: '/compare', title: 'Compare Rooflyzer', changefreq: 'weekly', priority: 0.9 },
  {
    path: '/compare/jobnimbus',
    title: 'Rooflyzer vs JobNimbus',
    changefreq: 'weekly',
    priority: 0.8,
  },
  {
    path: '/compare/acculynx',
    title: 'Rooflyzer vs AccuLynx',
    changefreq: 'weekly',
    priority: 0.8,
  },
  { path: '/compare/leap', title: 'Rooflyzer vs Leap', changefreq: 'weekly', priority: 0.8 },
  { path: '/compare/roofr', title: 'Rooflyzer vs Roofr', changefreq: 'weekly', priority: 0.8 },
  {
    path: '/marketing-dashboard',
    title: 'Marketing Dashboard',
    changefreq: 'weekly',
    priority: 0.85,
  },
  { path: '/marketing', title: 'Marketing', changefreq: 'weekly', priority: 0.75 },
  { path: '/nurture', title: 'Lead Nurturing', changefreq: 'weekly', priority: 0.85 },
  { path: '/estimator', title: 'Estimator', changefreq: 'weekly', priority: 0.9 },
  { path: '/demo', title: 'Demo', changefreq: 'weekly', priority: 0.9 },
  { path: '/pricing', title: 'Pricing', changefreq: 'weekly', priority: 0.9 },
  {
    path: '/enterprise',
    title: 'Enterprise',
    changefreq: 'weekly',
    priority: 0.85,
  },
  { path: '/about', title: 'About', changefreq: 'monthly', priority: 0.7 },
  { path: '/our-story', title: 'Our Story', changefreq: 'monthly', priority: 0.7 },
  { path: '/contact', title: 'Contact', changefreq: 'monthly', priority: 0.7 },
  {
    path: '/community/founders-beta-program',
    title: 'Founders Beta Program',
    changefreq: 'monthly',
    priority: 0.7,
  },
  { path: '/beta', title: 'Beta Program', changefreq: 'monthly', priority: 0.6 },
  { path: '/blog', title: 'Blog', changefreq: 'weekly', priority: 0.8 },
  { path: '/integrations', title: 'Integrations', changefreq: 'weekly', priority: 0.8 },
  { path: '/faq', title: 'FAQ', changefreq: 'monthly', priority: 0.7 },
  { path: '/changelog', title: 'Changelog', changefreq: 'weekly', priority: 0.7 },
  { path: '/privacy', title: 'Privacy Policy', changefreq: 'yearly', priority: 0.3 },
  { path: '/terms', title: 'Terms of Service', changefreq: 'yearly', priority: 0.3 },
  { path: '/security', title: 'Security', changefreq: 'monthly', priority: 0.5 },
  { path: '/data-freedom', title: 'Data Freedom Pledge', changefreq: 'monthly', priority: 0.5 },
  { path: '/eula', title: 'EULA', changefreq: 'yearly', priority: 0.2 },
  { path: '/brand', title: 'Brand Directory', changefreq: 'monthly', priority: 0.5 },
  { path: '/sitemap', title: 'Sitemap', changefreq: 'monthly', priority: 0.4 },
];

/** Flatten nested routes while omitting child references from sitemap entries. */
function flattenSitemapNodes(nodes: SitemapNode[]): SitemapNode[] {
  return nodes.flatMap((node) => [
    { ...node, children: undefined },
    ...(node.children ? flattenSitemapNodes(node.children) : []),
  ]);
}

/** Return the marketing routes eligible for sitemap generation. */
export function getSitemapRouteTree(): SitemapNode[] {
  return MARKETING_ROUTE_TREE;
}

/**
 * Generate robots.txt content from CMS settings
 */
export async function generateRobotsTxt(): Promise<string> {
  const scripts = await loadScriptsSettings();
  return scripts?.robots_txt || getDefaultRobotsTxt();
}

/**
 * Get default robots.txt content
 */
function getDefaultRobotsTxt(): string {
  return `User-agent: *
Allow: /
Disallow: /app/
Disallow: /admin/
Disallow: /api/
Disallow: /auth/
Disallow: /team/
Disallow: /superadmin/

# Marketing pages are indexable
Allow: /features
Allow: /features/lead-tracking
Allow: /features/canvassing-widget
Allow: /features/marketing-dashboard
Allow: /features/seed-catalogs-templates
Allow: /about
Allow: /our-story
Allow: /contact
Allow: /compare
Allow: /compare/buildertrend-alternative
Allow: /compare/jobnimbus-alternative
Allow: /for-roofers
Allow: /contractors
Allow: /agencies
Allow: /integrations
Allow: /faq
Allow: /estimator
Allow: /nurture
Allow: /demo
Allow: /why-rooflyzer
Allow: /changelog
Allow: /blog
Allow: /sitemap
Allow: /sitemap.xml
Allow: /community/founders-beta-program

Sitemap: https://rooflyzer.pro/sitemap.xml
`;
}

/**
 * Generate sitemap.xml from pages and blog posts
 */
export async function generateSitemap(): Promise<string> {
  const baseUrl = 'https://rooflyzer.pro';
  const currentDate = new Date().toISOString().split('T')[0];
  const pages = flattenSitemapNodes(getSitemapRouteTree());

  // Load blog posts from content/blog
  const blogPosts = await loadAllBlogPosts();

  let sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
`;

  // Add pages
  pages.forEach((page) => {
    sitemap += `  <url>
    <loc>${baseUrl}${page.path}</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>${page.changefreq || 'weekly'}</changefreq>
    <priority>${page.priority ?? 0.8}</priority>
  </url>
`;
  });

  // Add blog posts
  blogPosts.forEach((post) => {
    if (!post.draft) {
      sitemap += `  <url>
    <loc>${baseUrl}/blog/${post.slug}</loc>
    <lastmod>${new Date(post.date).toISOString().split('T')[0]}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.7</priority>
  </url>
`;
    }
  });

  sitemap += `</urlset>`;

  return sitemap;
}

/**
 * Load all blog posts (simplified - would need API or file listing)
 */
async function loadAllBlogPosts(): Promise<Array<{ slug: string; date: string; draft?: boolean }>> {
  // In production, this would fetch from content/blog directory
  // For now, return empty array
  return [];
}

/**
 * Get robots.txt route handler (for API route or static generation)
 */
export async function getRobotsTxtResponse(): Promise<Response> {
  const content = await generateRobotsTxt();
  return new Response(content, {
    headers: {
      'Content-Type': 'text/plain',
    },
  });
}

/**
 * Get sitemap.xml route handler (for API route or static generation)
 */
export async function getSitemapResponse(): Promise<Response> {
  const content = await generateSitemap();
  return new Response(content, {
    headers: {
      'Content-Type': 'application/xml',
    },
  });
}
